import { CloseButton } from '@eventbrite/eds-modal';
import { GenericLoader } from '@eventbrite/timed-entry';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import type { InfiniteCalendarSeriesDispatcher } from '../../actions/infiniteCalendarSeries';
import {
    SELECT_A_DAY_TO_CHECK_TIME_SLOTS,
    SELECT_A_TIME_TO_CHECK_TICKETS,
    SELECT_DAY_AND_TIME_TO_CHECK_TICKETS,
} from '../../constants';
import { PageTemplate, TemplateProps } from '../../containers/template';
import {
    AccordionSection,
    CalendarType,
    type InfiniteCalendarSeriesNamespace,
} from '../../state/infiniteCalendarSeries';
import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../utils/rebrand';
import { getCurrentOrFutureMonth } from '../../utils/timedEntry';
import { ConnectedEventInfoCard } from '../EventInfoCard/ConnectedEventInfoCard';
import {
    AccordionPanel,
    CalendarContainer,
    getFormattedTime,
    TicketsAccordionLoadingSkeleton,
} from '../TimedEntrySeries';
import { DateSvg } from '../TimedEntrySeries/assests/DateSvg';
import { RebrandedDateSvg } from '../TimedEntrySeries/assests/RebrandedDateSvg';
import { RebrandedTicketSvg } from '../TimedEntrySeries/assests/RebrandedTicketSvg';
import { RebrandedTimeSvg } from '../TimedEntrySeries/assests/RebrandedTimeSvg';
import { TicketSvg } from '../TimedEntrySeries/assests/TicketSvg';
import { TimeSvg } from '../TimedEntrySeries/assests/TimeSvg';
import {
    ACCORDION_SECTION_CONTAINER_ID,
    COLLAPSED_SECTION_ANIMATION_DURATION_MS,
} from '../TimedEntrySeries/constants';
import { TimeSlotContainer } from '../TimedEntrySeries/TimeSlotContainer';
import { TimeSlotLoadingSkeleton } from '../TimedEntrySeries/TimeSlotContainer/TimeSlotLoadingSkeleton/TimeSlotLoadingSkeleton';
import commonStyles from './InfiniteCalendarSeries.common.module.scss';
import defaultStyles from './InfiniteCalendarSeries.module.scss';
import rebrandStyles from './InfiniteCalendarSeries.rebranded.module.scss';
import {
    calcCollapsedSectionsHeight,
    CHOOSE_OPTIONS,
    getDateSectionTitle,
    getDisplayDate,
    getMonthAndYearFromDate,
    isAccordionSectionOpen,
    TICKETS,
    TIME,
} from './utils';

const stylesModules = {
    [DEFAULT_STYLES]: defaultStyles,
    [REBRANDING_STYLES]: rebrandStyles,
};
export interface InfiniteCalendarSeriesProps
    extends InfiniteCalendarSeriesNamespace,
        InfiniteCalendarSeriesDispatcher {}

export const InfiniteCalendarSeries: React.FC<InfiniteCalendarSeriesProps> = ({
    activeSection,
    calendarType,
    children,
    compactCalendarData,
    eventDateOverride,
    isAppRebrandActive,
    isDateSectionLoading,
    isEventOnline,
    isTicketSectionLoading,
    isTimeSectionLoading,
    locale,
    onDateSelect,
    onInitialLoad,
    onPageClose,
    onMonthLoad,
    onTimeSelect,
    onToggleSection,
    parentEventInfo,
    resetTimeSelection,
    scrollCalendarData,
    selectedDate,
    selectedSessionId,
    selectedTime,
    shouldShowTicketError,
    shouldShowTimeError,
    templateProps,
    timeSlots,
    timezone,
}) => {
    const theme = getStyles(isAppRebrandActive);
    const styles = stylesModules[theme];
    const [collapsedSectionsHeight, setCollapsedSectionsHeight] = useState(0);

    const { TicketsAccordionIcon, CalendarAccordionIcon, TimeAccordionIcon } =
        isAppRebrandActive
            ? {
                  TicketsAccordionIcon: RebrandedTicketSvg,
                  CalendarAccordionIcon: RebrandedDateSvg,
                  TimeAccordionIcon: RebrandedTimeSvg,
              }
            : {
                  TicketsAccordionIcon: TicketSvg,
                  CalendarAccordionIcon: DateSvg,
                  TimeAccordionIcon: TimeSvg,
              };

    const tickets = isTicketSectionLoading ? (
        <TicketsAccordionLoadingSkeleton />
    ) : (
        children
    );
    const timeSlotsSection = isTimeSectionLoading ? (
        <TimeSlotLoadingSkeleton />
    ) : (
        <TimeSlotContainer
            timeSlotData={timeSlots || []}
            timezone={timezone}
            onTimeSelect={onTimeSelect}
            locale={locale}
            isAppRebrandActive={isAppRebrandActive}
        />
    );
    const displayDate: string = getDisplayDate({
        activeSection,
        calendarType,
        date: selectedDate || '',
        isEventOnline,
        locale,
        timezone,
    });
    const displayTime: string =
        activeSection === AccordionSection.time || !selectedTime
            ? ''
            : getFormattedTime(selectedTime, locale, timezone);

    const ticketErrorText = !selectedDate
        ? SELECT_DAY_AND_TIME_TO_CHECK_TICKETS
        : SELECT_A_TIME_TO_CHECK_TICKETS;

    const isDateSectionOpen = isAccordionSectionOpen(
        activeSection,
        AccordionSection.date,
    );

    const isTicketsSectionOpen = isAccordionSectionOpen(
        activeSection,
        AccordionSection.tickets,
    );

    const isSingleDayEvent = calendarType === CalendarType.singleDayEvents;

    const isTimeSectionOpen = isAccordionSectionOpen(
        activeSection,
        AccordionSection.time,
    );

    const handleSectionToggle = (selectedSection: AccordionSection) => {
        onToggleSection({
            activeSection: selectedSection,
            selectedDate,
            selectedTime,
            calendarType,
        });
    };

    useEffect(() => {
        onInitialLoad();
    }, [onInitialLoad]);

    useEffect(() => {
        if (isDateSectionLoading) {
            return;
        }
        const hasCollapsibleTimeSection =
            calendarType === CalendarType.scrollCalendar ||
            calendarType === CalendarType.compactWithoutSession;
        const timeout = setTimeout(
            () => {
                setCollapsedSectionsHeight(calcCollapsedSectionsHeight());
            },
            hasCollapsibleTimeSection
                ? COLLAPSED_SECTION_ANIMATION_DURATION_MS
                : 0,
        );
        return () => clearTimeout(timeout);
    }, [
        calendarType,
        isDateSectionLoading,
        isDateSectionOpen,
        isSingleDayEvent,
        isTicketsSectionOpen,
        isTimeSectionOpen,
    ]);

    const timeAccordionPanel = (
        <AccordionPanel
            title={TIME}
            isExpanded={isTimeSectionOpen}
            onClick={() => handleSectionToggle(AccordionSection.time)}
            icon={<TimeAccordionIcon />}
            displayDateTime={displayTime}
            error={shouldShowTimeError}
            errorText={SELECT_A_DAY_TO_CHECK_TIME_SLOTS}
            shouldClubAccordionDateAndTimeSections={isTicketsSectionOpen}
            isAppRebrandActive={isAppRebrandActive}
        >
            {timeSlotsSection}
        </AccordionPanel>
    );

    const dateAndTimeSections = isSingleDayEvent ? (
        <>
            <div
                data-section-container-id={ACCORDION_SECTION_CONTAINER_ID}
                className={commonStyles.eventInfoCard}
            >
                <ConnectedEventInfoCard
                    onChange={
                        selectedSessionId
                            ? () => {
                                  handleSectionToggle(AccordionSection.time);
                                  resetTimeSelection();
                              }
                            : undefined
                    }
                    eventDateOverride={
                        !selectedSessionId || isTicketSectionLoading
                            ? eventDateOverride
                            : undefined
                    }
                    showEventPrice={false}
                />
            </div>
            {!selectedSessionId ? timeAccordionPanel : null}
        </>
    ) : (
        <>
            <AccordionPanel
                title={getDateSectionTitle(
                    calendarType || CalendarType.scrollCalendar,
                )}
                isExpanded={isDateSectionOpen}
                onClick={() => {
                    handleSectionToggle(AccordionSection.date);
                }}
                icon={<CalendarAccordionIcon />}
                displayDateTime={displayDate}
                isAppRebrandActive={isAppRebrandActive}
            >
                <CalendarContainer
                    calendarType={calendarType}
                    scrollCalendarData={scrollCalendarData}
                    compactCalendarData={compactCalendarData}
                    locale={locale}
                    onMonthLoad={(monthAndYear) => {
                        onMonthLoad(
                            monthAndYear.month,
                            monthAndYear.year,
                            timezone,
                        );
                    }}
                    onDateSelect={onDateSelect}
                    isAppRebrandActive={isAppRebrandActive}
                    initialSelectedDate={selectedDate || ''}
                    firstMonth={{
                        ...getCurrentOrFutureMonth(
                            parentEventInfo.startUTCDate,
                        ),
                    }}
                    lastMonth={{
                        ...getMonthAndYearFromDate(parentEventInfo.endUTCDate),
                    }}
                />
            </AccordionPanel>
            {calendarType !== CalendarType.compactWithSession ? (
                <>{timeAccordionPanel}</>
            ) : null}
        </>
    );

    return (
        <PageTemplate
            notificationOptions={[]}
            templateProps={templateProps as TemplateProps}
            isAppRebrandActive={isAppRebrandActive}
            containerClassName={commonStyles.pageTemplateContainer}
        >
            <div
                className={classnames(commonStyles.container, styles.container)}
                style={
                    {
                        '--collapsed-sections-height': `${collapsedSectionsHeight}px`,
                    } as React.CSSProperties
                }
            >
                <header className={commonStyles.headerContainer}>
                    <p className={styles.headerText}>{CHOOSE_OPTIONS}</p>
                    <CloseButton
                        customClassName={commonStyles.closeButton}
                        onClose={onPageClose}
                    />
                </header>
                {isDateSectionLoading ? (
                    <GenericLoader />
                ) : (
                    <div className={commonStyles.contentContainer}>
                        {dateAndTimeSections}
                        <AccordionPanel
                            title={TICKETS}
                            isExpanded={isTicketsSectionOpen}
                            onClick={() =>
                                handleSectionToggle(AccordionSection.tickets)
                            }
                            icon={<TicketsAccordionIcon />}
                            error={shouldShowTicketError}
                            errorText={ticketErrorText}
                            isAppRebrandActive={isAppRebrandActive}
                            className={classnames({
                                [commonStyles.ticketsPanelContainer]:
                                    isTicketsSectionOpen,
                            })}
                            collapsibleContainerClassName={
                                commonStyles.ticketsContentContainer
                            }
                        >
                            {tickets}
                        </AccordionPanel>
                    </div>
                )}
            </div>
        </PageTemplate>
    );
};
